// src/components/WorkTimeModal.js
import React, { useState, useEffect } from 'react';
import { API_URL } from "../config";
import './WorkTimeModal.css';

const timeOptions = [];
for (let h = 0; h < 25; h++) { // do 24:00 włącznie
  const val = `${String(h).padStart(2, '0')}:00`;
  timeOptions.push(val);
}

function calcHours(start, end) {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  let startMinutes = sh * 60 + sm;
  let endMinutes = eh * 60 + em;
  let diff = (endMinutes - startMinutes) / 60;
  if (diff < 0) diff += 24; // Obsługa pracy przez północ (opcjonalnie, można wyłączyć)
  return Math.round(diff * 2) / 2; // Zaokrąglenie do 0.5
}

const WorkTimeModal = ({ orderId, onClose, onSuccess }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [workDate, setWorkDate] = useState('');
  const [startTime, setStartTime] = useState('07:00');
  const [endTime, setEndTime] = useState('15:00');
  const [error, setError] = useState('');

  // Domyślny dzień to dziś
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setWorkDate(today);
  }, []);

  // Pobierz pracowników
  useEffect(() => {
    // Używamy pełnego adresu z configu
    fetch(`${API_URL}/orders/workers`, {
      credentials: "include"
    })
      .then(response => {
        // 1. Sprawdź status HTTP (czy jest 200 OK)
        if (!response.ok) {
           throw new Error(`Błąd HTTP: ${response.status}`); 
        }
        return response.json();
      })
      .then(data => {
        // 2. Sprawdź czy data to na pewno tablica
        if (Array.isArray(data)) {
            setEmployees(data);
        } else {
            console.error("Otrzymano błędne dane (nie tablicę):", data);
            // Opcjonalnie: obsługa błędu, np. przekierowanie do logowania
        }
      })
      .catch(err => console.error("Błąd pobierania pracowników:", err));
  }, []);

  const handleCheckboxChange = (e, employeeId) => {
    if (e.target.checked) {
      setSelectedEmployees(prev => [...prev, employeeId]);
    } else {
      setSelectedEmployees(prev => prev.filter(id => id !== employeeId));
    }
  };

  const hours = calcHours(startTime, endTime);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedEmployees.length === 0) {
      setError("Wybierz co najmniej jednego pracownika.");
      return;
    }
    if (!startTime || !endTime) {
      setError("Wybierz godziny rozpoczęcia i zakończenia pracy.");
      return;
    }
    if (hours <= 0 || hours > 15 || isNaN(hours)) {
      setError("Czas pracy musi być w zakresie 0.5-15 godzin.");
      return;
    }
    // Walidacja daty – nie może być w przyszłości
    const today = new Date().setHours(0, 0, 0, 0);
    const selectedDate = new Date(workDate).setHours(0, 0, 0, 0);
    if (selectedDate > today) {
      setError("Data wykonania pracy nie może być w przyszłości.");
      return;
    }
    // Walidacja: godzina końca nie wcześniej niż początek (jeśli nie obsługujemy przez północ)
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const startTotal = sh * 60 + sm;
    const endTotal = eh * 60 + em;
    if (endTotal <= startTotal) {
      setError("Godzina zakończenia musi być późniejsza niż rozpoczęcia.");
      return;
    }
    setError('');

	const payload = {
	  employeeIds: selectedEmployees,
	  start_time: startTime,
	  end_time: endTime,
	  work_date: workDate
	};

    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/working_time`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Błąd przy dodawaniu czasu pracy.");
        return;
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Błąd:", err);
      setError("Wewnętrzny błąd serwera.");
    }
  };

  return (
    <div className="worktime-modal-overlay">
      <div className="worktime-modal">
        <h3>Formularz Czasu Pracy</h3>
        <form onSubmit={handleSubmit}>
          <div className="worktime-form-group">
            <label>Pracownik:</label>
            <div className="employee-list">
              {employees.map(emp => (
                <div key={emp.id} className="employee-item">
                  <label>
                    <input
                      type="checkbox"
                      value={emp.id}
                      onChange={(e) => handleCheckboxChange(e, emp.id)}
                      checked={selectedEmployees.includes(emp.id)}
                    />
                    {emp.first_name} {emp.last_name}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="worktime-form-group">
            <label>Data Wykonania Pracy:</label>
            <input
              type="date"
              value={workDate}
              onChange={(e) => setWorkDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              required
            />
          </div>
          <div className="worktime-form-group" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <label>Godzina rozpoczęcia:</label>
              <select value={startTime} onChange={e => setStartTime(e.target.value)} required>
                {timeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label>Godzina zakończenia:</label>
              <select value={endTime} onChange={e => setEndTime(e.target.value)} required>
                {timeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </div>
          </div>
          <div className="worktime-form-group">
            <label>Liczba godzin (liczone automatycznie):</label>
            <input
              type="text"
              value={hours > 0 ? hours : ''}
              readOnly
              style={{ background: '#eee', color: '#222' }}
            />
          </div>
          {error && <p className="worktime-error-message">{error}</p>}
          <div className="worktime-modal-buttons">
            <button type="button" className="worktime-btn-submit" onClick={onClose}>Anuluj</button>
            <button type="submit" className="worktime-btn-submit">Dodaj Czas Pracy</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkTimeModal;
